/**
 * POP! — racine du frontend.
 *
 * Routage volontairement simple (pas de dépendance routeur) : accueil ou
 * salle. La session de jeu (code + playerId) est conservée en sessionStorage
 * pour survivre à un rechargement : le socket se reconnecte et redemande
 * l'état, le joueur reprend sa partie.
 *
 * Le pied de page rappelle discrètement la release réellement servie par
 * l'API (héritage de l'écran A0) : après un déploiement puis un rollback,
 * cette valeur doit changer, puis revenir.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { versionSchema } from '@pop/contracts';
import { Home } from './screens/Home.js';
import { Room } from './screens/Room.js';

type Route =
  | { readonly name: 'home' }
  | { readonly name: 'room'; readonly code: string; readonly playerId: string };

const SESSION_KEY = 'pop:session';

function readSession(): Route {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (raw === null) {
      return { name: 'home' };
    }
    const parsed: unknown = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) {
      return { name: 'home' };
    }
    const record = parsed as { readonly code?: unknown; readonly playerId?: unknown };
    if (typeof record.code !== 'string' || typeof record.playerId !== 'string') {
      return { name: 'home' };
    }
    return { name: 'room', code: record.code, playerId: record.playerId };
  } catch {
    return { name: 'home' };
  }
}

/** Rappel discret de la release servie — invisible si l'API ne répond pas. */
function ReleaseFooter(): JSX.Element {
  const [release, setRelease] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // URL relative : même origine en local comme en production.
    fetch('/api/version')
      .then(async (response): Promise<string | null> => {
        if (!response.ok) {
          return null;
        }
        const parsed = versionSchema.safeParse((await response.json()) as unknown);
        return parsed.success ? parsed.data.release : null;
      })
      .then((value) => {
        if (!cancelled && value !== null) {
          setRelease(value);
        }
      })
      .catch(() => {
        setRelease(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <footer className="app-footer">{release !== null ? `POP! • ${release}` : 'POP!'}</footer>;
}

export function App() {
  const [route, setRoute] = useState<Route>(() => readSession());

  const handleEnter = useCallback((code: string, playerId: string) => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ code, playerId }));
    } catch {
      // Stockage indisponible (navigation privée stricte…) : la partie
      // fonctionne quand même, sans reprise au rechargement.
    }
    setRoute({ name: 'room', code, playerId });
  }, []);

  const handleLeave = useCallback(() => {
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // Déjà géré ci-dessus : on rentre à l'accueil dans tous les cas.
    }
    setRoute({ name: 'home' });
  }, []);

  return (
    <div className="app">
      <main className="app-main">
        {route.name === 'home' ? (
          <Home onEnter={handleEnter} />
        ) : (
          <Room code={route.code} playerId={route.playerId} onLeave={handleLeave} />
        )}
      </main>
      <ReleaseFooter />
    </div>
  );
}
